import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/feature/DramaCard";
import SectionHeader from "@/components/feature/SectionHeader";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { categories, dramas, siteStats } from "@/mocks/dramas";
import { usePageMeta } from "@/hooks/usePageMeta";

const hotDramas = [...dramas].sort((a, b) => b.heat - a.heat).slice(0, 8);
const rankList = [...dramas].sort((a, b) => b.heat - a.heat).slice(0, 6);
const freshDramas = dramas.filter((item) => item.year === 2025).slice(0, 4);
const analysisList = dramas.slice(0, 3);

export default function Home() {
  usePageMeta({
    title: "波多野结衣家庭教师 - 热门电视剧在线观看与剧情介绍",
    description:
      "波多野结衣家庭教师网站提供最新热门电视剧在线观看资源，并包含详细剧情介绍和角色分析，满足用户全面了解剧情需求。我们致力于为影视爱好者打造优质的影视内容平台。",
    keywords:
      "波多野结衣家庭教师,电视剧在线观看,剧情介绍,影视内容,热门电视剧,角色分析,剧情解析",
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <h1 className="sr-only">波多野结衣家庭教师 - 热门电视剧在线观看与剧情介绍</h1>
        <HeroCarousel />

        <section className="border-y border-background-200/70 bg-background-100">
          <div className="mx-auto grid w-full max-w-[1240px] grid-cols-2 gap-y-6 px-4 py-7 md:grid-cols-4 md:px-6 md:py-8">
            {siteStats.map((stat) => (
              <div
                key={stat.label}
                className="flex flex-col items-center border-background-200/70 px-4 text-center md:border-l md:first:border-l-0"
              >
                <p className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                  {stat.value}
                  <span className="ml-1 font-label text-xs font-normal text-accent-600">
                    {stat.suffix}
                  </span>
                </p>
                <p className="mt-1 font-label text-[11px] tracking-[0.18em] text-foreground-500">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1240px] px-4 py-14 md:px-6 md:py-20">
          <SectionHeader
            eyebrow="分类快捷入口"
            title="按类型找到今晚要追的那部剧"
            description="从都市情感到古装权谋，六个主分类覆盖站内全部剧集标签，点击即可带着筛选条件进入分类页。"
            linkTo="/category"
            linkLabel="全部分类"
          />
          <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {categories.map((category) => (
              <Link
                key={category.name}
                to={`/category?type=${encodeURIComponent(category.name)}`}
                className="group flex cursor-pointer flex-col rounded-lg border border-background-200/70 bg-background-100 p-4 transition-colors hover:border-primary-500/50 hover:bg-background-200/60 md:p-5"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500/12 text-primary-500">
                  <i className={`${category.icon} text-lg`}></i>
                </span>
                <span className="mt-3 font-heading text-base font-semibold text-foreground-950">
                  {category.name}
                </span>
                <span className="mt-1 font-label text-[11px] text-foreground-500">
                  {category.count} 部在库
                </span>
                <span className="mt-3 flex items-center gap-1 font-label text-[11px] text-foreground-600 transition-colors group-hover:text-accent-500">
                  进入分类
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="border-y border-background-200/60 bg-background-100/50">
          <div className="mx-auto w-full max-w-[1240px] px-4 py-14 md:px-6 md:py-20">
            <SectionHeader
              eyebrow="热门推荐"
              title="这周被讨论最多的八部剧"
              description="依据站内热度与剧迷评分综合排序，每部剧都配有完整剧情梗概与角色分析。"
              linkTo="/category"
              linkLabel="查看全部剧集"
            />
            <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-6 lg:grid-cols-4">
              {hotDramas.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1240px] px-4 py-14 md:px-6 md:py-20">
          <SectionHeader
            eyebrow="热播榜单"
            title="追剧热度榜 · 实时更新"
            description="热度由站内浏览、收藏与讨论量合并计算，仅作追剧参考。"
          />
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="flex flex-col gap-2.5 lg:col-span-2">
              {rankList.map((drama, i) => (
                <Link
                  key={drama.id}
                  to={`/detail/${drama.id}`}
                  className="group flex cursor-pointer items-center gap-3 rounded-lg border border-background-200/70 bg-background-100/70 p-3 transition-colors hover:border-primary-500/45 hover:bg-background-100 md:gap-4"
                >
                  <span
                    className={`w-7 shrink-0 text-center font-heading text-xl font-semibold md:text-2xl ${
                      i < 3 ? "text-primary-500" : "text-foreground-500"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={drama.cover}
                      alt={`${drama.title} 剧集封面`}
                      title={`${drama.title} 剧情介绍`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                      {drama.title}
                    </h3>
                    <p className="mt-1 line-clamp-1 text-xs text-foreground-600">{drama.tagline}</p>
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-2 font-label text-[11px] text-foreground-500">
                      <span>{drama.types.join(" / ")}</span>
                      <span className="text-foreground-500">·</span>
                      <span>{drama.episodeCount} 集</span>
                      <span className="text-foreground-500">·</span>
                      <span className="text-accent-600">热度 {drama.heat}</span>
                    </p>
                  </div>
                  <i className="ri-arrow-right-s-line hidden text-lg text-foreground-500 transition-colors group-hover:text-primary-500 sm:block"></i>
                </Link>
              ))}
            </div>

            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                    <span className="flex h-4 w-4 items-center justify-center text-accent-500">
                      <i className="ri-flashlight-line text-sm"></i>
                    </span>
                    新剧速递
                  </h3>
                  <span className="font-label text-[11px] text-foreground-500">2025 上线</span>
                </div>
                <ul className="mt-4 space-y-3">
                  {freshDramas.map((drama) => (
                    <li key={drama.id}>
                      <Link
                        to={`/detail/${drama.id}`}
                        className="group flex cursor-pointer items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm text-foreground-900 transition-colors group-hover:text-primary-500">
                            {drama.title}
                          </p>
                          <p className="mt-0.5 font-label text-[11px] text-foreground-500">
                            {drama.types[0]} · {drama.region} · {drama.status}
                          </p>
                        </div>
                        <span className="shrink-0 font-label text-[11px] text-accent-600">
                          ★ {drama.rating.toFixed(1)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/search"
                  className="mt-5 flex cursor-pointer items-center justify-center gap-1.5 rounded-md border border-background-300/60 py-2 font-label text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500"
                >
                  <i className="ri-search-line text-sm"></i>
                  搜索更多剧集
                </Link>
              </div>

              <div className="flex flex-1 flex-col justify-between rounded-lg border border-primary-500/25 bg-primary-50 p-5">
                <div>
                  <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-500">
                    编辑手记
                  </p>
                  <p className="mt-2.5 font-heading text-[15px] leading-relaxed text-foreground-900">
                    “好看的剧不止有一个结局，值得被写下来的，往往是那些没有说出口的选择。”
                  </p>
                </div>
                <Link
                  to="/about"
                  className="mt-5 flex cursor-pointer items-center gap-1.5 font-label text-xs text-accent-600 transition-colors hover:text-accent-500"
                >
                  了解我们的内容标准
                  <i className="ri-arrow-right-line text-sm"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-background-200/60 bg-background-100/40">
          <div className="mx-auto w-full max-w-[1240px] px-4 py-14 md:px-6 md:py-20">
            <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
              <div className="relative overflow-hidden rounded-lg border border-background-200/70">
                <div className="aspect-[4/3] w-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=Cinematic%20editorial%20still%20of%20a%20dim%20private%20screening%20room%20with%20rows%20of%20empty%20velvet%20seats%2C%20a%20warm%20projector%20light%20beam%20cutting%20through%20dust%20in%20the%20air%2C%20deep%20ink%20shadows%2C%20film%20analysis%20column%20mood%2C%20subtle%20grain%2C%20no%20text&width=1200&height=900&seq=editorial-screening-room&orientation=landscape"
                    alt="影视剧情解析专栏场景"
                    title="剧情解析专栏 深度阅读"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-background-50/85 via-transparent to-transparent"></div>
                <span className="absolute bottom-4 left-4 rounded-full bg-background-50/75 px-3 py-1 font-label text-[11px] text-accent-500">
                  剧情解析 · 编辑部长评
                </span>
              </div>

              <div>
                <SectionHeader
                  eyebrow="深度阅读"
                  title="不止剧情简介，我们把故事拆开来看"
                  description="叙事结构、人物动机、镜头语言——每一篇解析都希望帮你在关掉播放器之后，还能多想一会儿。"
                />
                <ul className="mt-6 divide-y divide-background-200/70 border-y border-background-200/70">
                  {analysisList.map((drama, i) => (
                    <li key={drama.id}>
                      <Link
                        to={`/detail/${drama.id}`}
                        className="group flex cursor-pointer items-start gap-4 py-4"
                      >
                        <span className="mt-0.5 font-label text-xs text-primary-500">
                          0{i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-500">
                            《{drama.title}》：{drama.tagline}
                          </p>
                          <p className="mt-1 line-clamp-1 text-xs text-foreground-600">
                            {drama.types.join(" / ")} · 导演 {drama.director}
                          </p>
                        </div>
                        <i className="ri-arrow-right-up-line mt-1 text-base text-foreground-500 transition-colors group-hover:text-accent-500"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/category"
                  className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  进入剧集库
                  <i className="ri-arrow-right-line text-sm"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}